// @flow

import * as React from 'react';
import ReactModal from 'react-modal';
import cx from 'classnames';
import i18n from 'app/common/lib/i18n';
import Impressions from 'app/packages/impression-logging';
import StopEventPropagation from 'app/packages/stop-event-propagation/StopEventPropagation';

// kathy: I know this is strange to have the Gestalt spinner in the pinboard modal, but it's currently for
// Perceived performance. Please use the Gestalt <Modal /> for new features going forward.
import { Icon, Spinner } from 'gestalt';

// Remove react-modal default styles
ReactModal.defaultStyles = {};

type Props = {|
  allowClickAndDrag?: boolean,
  children?: React.Node,
  // Temporary className prop to maintain backwards compability with markup.
  className?: string,
  loading?: boolean,
  noButton?: boolean,
  onAfterOpen?: () => void,
  onHide?: ?() => void,
  style?: {
    overlay?: { [any]: string | number },
    content?: { [any]: string | number },
  },
  type?: string,
|};

class LegacyModal extends React.Component<Props> {
  componentDidMount() {
    // End active impressions when a Modal is opened
    this.pauseImpressionsTracking();
  }

  componentWillUnmount() {
    this.resumeImpressionsTracking();
  }

  pauseImpressionsTracking = () => {
    Impressions.pause();
  };

  resumeImpressionsTracking = () => {
    Impressions.resume();
  };

  handleHide = () => {
    // Restart visible impressions when a Modal is closed
    this.resumeImpressionsTracking();

    if (this.props.onHide) {
      this.props.onHide();
    }
  };

  handleOpen = () => {
    // End active impressions when a Modal is opened
    this.pauseImpressionsTracking();

    if (this.props.onAfterOpen) {
      this.props.onAfterOpen();
    }
  };

  render() {
    const {
      allowClickAndDrag,
      className,
      loading,
      noButton,
      onHide,
      type,
      style = {},
    } = this.props;

    const modalClasses = cx(
      className,
      // Disable the focus style on the react modal since it's obvious that it's in focus.
      'dangerouslyDisableFocusStyle',
      'Module',
      'ReactModal__Content',
      'inModal',
      { modalHasClose: !!onHide },
      { ConfirmDialog: type === 'confirm' },
      { PushNotificationSettings: type === 'notifications' },
    );

    const spinner = loading ? (
      <Spinner
        accessibilityLabel={i18n._(
          'Loading content',
          'Accessibility label for loading modal content',
        )}
        show
      />
    ) : null;

    const closeButton =
      onHide && !noButton ? (
        <button
          className="Button Module borderless cancelButton closeModal inModal show"
          onClick={this.handleHide}
          type="button"
        >
          <Icon icon="cancel" accessibilityLabel={i18n._('Close modal')} size={20} />
        </button>
      ) : null;

    return (
      // Preventing all supported SyntheticEvents from bubbling (except onFocus/onBlur, see BUG-78260)
      // because react-modal now leverages React 16's new Portal API
      // which allows for event bubbling.
      // This maintains the legacy behavior and keeps event bubbling similar to _unstable_renderSubtreeIntoContainer
      // Event bubbling change: https://reactjs.org/docs/portals.html#event-bubbling-through-portals
      // Discussion on gh: https://github.com/facebook/react/issues/11387
      // List of all supported events: https://reactjs.org/docs/events.html#supported-events
      <StopEventPropagation allowClickAndDrag={allowClickAndDrag}>
        <ReactModal
          ariaHideApp={false}
          className={modalClasses}
          isOpen
          onAfterOpen={this.handleOpen}
          onRequestClose={this.handleHide}
          shouldCloseOnOverlayClick
          style={style}
        >
          {spinner}
          {this.props.children}
          {closeButton}
        </ReactModal>
      </StopEventPropagation>
    );
  }
}

export default LegacyModal;
